import React, { createContext, useEffect, useMemo, useState } from 'react'
import isBoolean from 'lodash-es/isBoolean'
import { useFeatureFlags } from 'hooks/useFeatureFlags'

const DARK_MODE_STORAGE_KEY = 'darkMode'
const STORAGE_EVENT_NAME = 'storage'

export interface ThemeContext {
  darkMode: boolean
  setDarkMode: React.Dispatch<React.SetStateAction<boolean>>
}

export const ThemeContext = createContext<ThemeContext>({
  darkMode: false,
  setDarkMode: () => false,
})

interface ThemeProviderProps {
  children: React.ReactNode
}

export const ThemeProvider = ({ children }: ThemeProviderProps) => {
  const { enableDarkMode } = useFeatureFlags()

  const [darkMode, setDarkMode] = useState(() => {
    if (isBoolean(enableDarkMode) && !enableDarkMode) {
      return false
    }

    const stored = localStorage.getItem(DARK_MODE_STORAGE_KEY)
    if (stored === 'true') return true
    if (stored === 'false') return false
    return globalThis.matchMedia('(prefers-color-scheme: dark)').matches
  })

  useEffect(() => {
    if (isBoolean(enableDarkMode) && !enableDarkMode) {
      setDarkMode(false)
      return
    }

    const handleStorageChange = (event: StorageEvent) => {
      if (event?.key === DARK_MODE_STORAGE_KEY && event?.newValue) {
        if (event.newValue === 'true') setDarkMode(true)
        else if (event.newValue === 'false') setDarkMode(false)
      }
    }

    globalThis.addEventListener(STORAGE_EVENT_NAME, handleStorageChange)
    return () => {
      globalThis.removeEventListener(STORAGE_EVENT_NAME, handleStorageChange)
    }
  }, [enableDarkMode])

  useEffect(() => {
    if (darkMode) {
      document.documentElement.dataset.theme = 'dark'
    } else {
      delete document.documentElement.dataset.theme
    }

    if (isBoolean(darkMode)) {
      localStorage.setItem(DARK_MODE_STORAGE_KEY, JSON.stringify(darkMode))
    }
  }, [darkMode])

  useEffect(() => {
    const stored = localStorage.getItem(DARK_MODE_STORAGE_KEY)
    if (stored === 'null' || stored === 'undefined') {
      localStorage.removeItem(DARK_MODE_STORAGE_KEY)
    }
  }, [])

  const value = useMemo(() => {
    return {
      darkMode: darkMode,
      setDarkMode: setDarkMode,
    }
  }, [darkMode, setDarkMode])

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}
